
import React, { useState, useRef, useMemo } from 'react';
import { 
  Finding, 
  FollowUpStatus,
  Equipment, 
  InspectionActual,
  AppUser,
  UserRole
} from '../types';
import { 
  Camera,
  X,
  Plus, // Fixed: Added missing Plus icon import
  ShieldCheck,
  PenTool,
  Zap,
  ShieldAlert,
  CheckCircle2,
  Users,
  Calendar,
  Filter,
  ArrowRight,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  FileSearch,
  Image as ImageIcon,
  Loader2
} from 'lucide-react';

interface FindingsProps {
  findings: Finding[];
  equipments: Equipment[];
  actuals: InspectionActual[];
  updateFinding: (id: string, updates: Partial<Finding>) => void;
  user: AppUser;
}

/**
 * Utility to compress image to stay under Firestore 1MB limit.
 * Targets ~200-400KB to be safe.
 */
const compressImage = (base64Str: string, maxWidth = 800, quality = 0.5): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64Str;
    img.onerror = reject;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error("Canvas context failed"));
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
  });
};

const Findings: React.FC<FindingsProps> = ({ findings, equipments, actuals, updateFinding, user }) => {
  const [filterMonth, setFilterMonth] = useState<number>(new Date().getMonth());
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const [editingCAPA, setEditingCAPA] = useState<Finding | null>(null);
  const [reviewingCAPA, setReviewingCAPA] = useState<Finding | null>(null);
  const [adminNote, setAdminNote] = useState('');
  
  const [capaForm, setCapaForm] = useState({
    correctiveAction: '',
    correctivePhoto: '',
    preventiveAction: '',
    preventivePhoto: ''
  });

  const [isCompressing, setIsCompressing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [currentUploadTarget, setCurrentUploadTarget] = useState<'corrective' | 'preventive' | null>(null);

  const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
  const currentYear = new Date().getFullYear();
  const years = useMemo(() => Array.from({ length: 10 }, (_, i) => currentYear - 5 + i), [currentYear]);

  const isAdmin = user.role === UserRole.SUPER_ADMIN || user.role === UserRole.ADMIN;

  const filteredFindings = useMemo(() => {
    return findings.filter(f => {
      const targetDate = new Date(f.targetDate);
      return (
        targetDate.getMonth() === filterMonth &&
        targetDate.getFullYear() === filterYear &&
        (statusFilter === 'ALL' || f.status === statusFilter)
      );
    });
  }, [findings, filterMonth, filterYear, statusFilter]);

  const triggerPhotoUpload = (target: 'corrective' | 'preventive') => {
    setCurrentUploadTarget(target);
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
      photoInputRef.current.click();
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && currentUploadTarget) {
      setIsCompressing(true);
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result as string;
          const compressed = await compressImage(base64);
          setCapaForm(prev => ({ 
            ...prev, 
            [currentUploadTarget === 'corrective' ? 'correctivePhoto' : 'preventivePhoto']: compressed
          }));
        } catch (err) {
          console.error("Compression failed", err);
          alert("Gagal memproses gambar.");
        } finally {
          setIsCompressing(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditCAPA = (finding: Finding) => {
    setEditingCAPA(finding);
    setCapaForm({
      correctiveAction: finding.correctiveAction || '',
      correctivePhoto: finding.correctivePhoto || '',
      preventiveAction: finding.preventiveAction || '',
      preventivePhoto: finding.preventivePhoto || ''
    });
  };

  const submitClosureRequest = async () => {
    if (editingCAPA) {
      if (!capaForm.correctiveAction || !capaForm.correctivePhoto || !capaForm.preventiveAction || !capaForm.preventivePhoto) {
        alert("Wajib melampirkan foto bukti dan narasi perbaikan untuk kedua kategori!");
        return;
      }

      if (!window.confirm("Kirim laporan CAPA ini untuk diverifikasi admin?")) return;

      setIsSubmitting(true);
      try {
        await updateFinding(editingCAPA.id, {
          ...capaForm,
          status: FollowUpStatus.PENDING_APPROVAL,
          adminNote: '' 
        });
        setEditingCAPA(null);
        alert("Laporan berhasil dikirim ke Admin.");
      } catch (err) {
        console.error("Submit error:", err);
        alert("Gagal mengirim laporan. Coba lagi.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleReviewAction = async (newStatus: FollowUpStatus) => {
    if (reviewingCAPA) {
      if (newStatus === FollowUpStatus.OPEN && !adminNote.trim()) {
        alert("Berikan catatan feedback jika menolak laporan.");
        return;
      }

      const msg = newStatus === FollowUpStatus.CLOSED ? "Approve dan tutup temuan?" : "Tolak laporan ini?";
      if (!window.confirm(msg)) return;

      try {
        await updateFinding(reviewingCAPA.id, {
          status: newStatus,
          adminNote: newStatus === FollowUpStatus.OPEN ? adminNote : ''
        });
        setReviewingCAPA(null);
        setAdminNote('');
      } catch (err) {
        alert("Gagal memproses review.");
      }
    }
  };

  const handleDirectClose = (id: string) => {
    if (confirm("Tutup temuan secara langsung tanpa verifikasi?")) {
      updateFinding(id, { status: FollowUpStatus.CLOSED });
    }
  };

  return (
    <div className="space-y-12 pb-20 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">CAPA TRACKING</h1>
          <p className="text-slate-500 mt-2 font-medium italic">Manajemen Tindakan Perbaikan Temuan Audit.</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-3xl border border-slate-100 shadow-sm">
            <div className="flex items-center gap-2 border-r pr-3">
              <Calendar className="w-4 h-4 text-slate-400" />
              <select value={filterMonth} onChange={e => setFilterMonth(parseInt(e.target.value))} className="bg-transparent text-[10px] font-black uppercase outline-none cursor-pointer">
                {monthNames.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <select value={filterYear} onChange={e => setFilterYear(parseInt(e.target.value))} className="bg-transparent text-[10px] font-black uppercase outline-none cursor-pointer">
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-transparent text-[10px] font-black uppercase outline-none cursor-pointer">
                <option value="ALL">Semua Status</option>
                <option value={FollowUpStatus.OPEN}>🔴 OPEN</option>
                <option value={FollowUpStatus.PENDING_APPROVAL}>🟡 PENDING</option>
                <option value={FollowUpStatus.CLOSED}>🟢 CLOSED</option>
              </select>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-12">
        {filteredFindings.map((finding) => {
          const actual = actuals.find(a => a.id === finding.inspectionId);
          
          return (
            <div key={finding.id} className="bg-white rounded-[4rem] border border-slate-200 overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 group">
              <div className={`px-10 py-6 flex flex-col md:flex-row justify-between items-center gap-6 ${finding.status === FollowUpStatus.PENDING_APPROVAL ? 'bg-amber-400' : finding.status === FollowUpStatus.CLOSED ? 'bg-emerald-600' : 'bg-slate-950'} text-white`}>
                <div className="flex items-center gap-10">
                    <div>
                        <p className={`text-[9px] font-black uppercase ${finding.status === FollowUpStatus.PENDING_APPROVAL ? 'text-amber-900/60' : 'text-white/40'} tracking-widest mb-1`}>Status</p>
                        <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase border ${finding.status === FollowUpStatus.PENDING_APPROVAL ? 'bg-amber-900/10 border-amber-900/20 text-amber-950' : 'bg-white/10 border-white/20 text-white'}`}>
                            {finding.status}
                        </div>
                    </div>
                    <div>
                        <p className={`text-[9px] font-black uppercase ${finding.status === FollowUpStatus.PENDING_APPROVAL ? 'text-amber-900/60' : 'text-white/40'} tracking-widest mb-1`}>Penanggung Jawab (PIC)</p>
                        <p className={`text-sm font-black tracking-tight flex items-center gap-2 ${finding.status === FollowUpStatus.PENDING_APPROVAL ? 'text-amber-950' : 'text-white'}`}>
                            <Users className="w-4 h-4" /> {finding.pic}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                  {isAdmin && (
                    <div className="flex gap-2">
                       {finding.status === FollowUpStatus.PENDING_APPROVAL ? (
                         <button 
                            onClick={() => setReviewingCAPA(finding)} 
                            className="px-8 py-3 bg-white text-slate-900 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl hover:scale-105 transition-all flex items-center gap-2 border border-slate-200"
                          >
                            <FileSearch className="w-4 h-4 text-emerald-500" /> Review
                         </button>
                       ) : finding.status === FollowUpStatus.OPEN ? (
                         <button 
                            onClick={() => handleDirectClose(finding.id)} 
                            className="px-6 py-3 bg-emerald-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl hover:bg-emerald-600 transition-all flex items-center gap-2"
                          >
                            <CheckCircle2 className="w-4 h-4" /> Direct Closed
                         </button>
                       ) : null}
                    </div>
                  )}

                  {!isAdmin && finding.status === FollowUpStatus.OPEN && (
                    <button 
                      onClick={() => handleEditCAPA(finding)} 
                      className="px-8 py-3 bg-white text-slate-950 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl hover:scale-105 transition-all flex items-center gap-2"
                    >
                      <PenTool className="w-4 h-4 text-indigo-500" /> Update CAPA
                    </button>
                  )}
                </div>
              </div>

              {finding.adminNote && finding.status === FollowUpStatus.OPEN && (
                <div className="mx-10 mt-6 p-6 bg-rose-50 border border-rose-100 rounded-[2rem] flex items-start gap-4 animate-in slide-in-from-top-4">
                  <div className="w-10 h-10 bg-rose-500 text-white rounded-xl flex items-center justify-center shrink-0 shadow-lg shadow-rose-500/20"><MessageSquare className="w-5 h-5" /></div>
                  <div className="flex-1">
                    <p className="text-[10px] font-black text-rose-600 uppercase tracking-widest mb-1">Feedback Admin:</p>
                    <p className="text-sm font-bold text-rose-900 italic leading-relaxed">"{finding.adminNote}"</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 divide-x divide-slate-100">
                <div className="p-10 space-y-6">
                    <div className="flex items-center gap-3 mb-2">
                        <ShieldAlert className="w-5 h-5 text-rose-500" />
                        <h4 className="text-[11px] font-black uppercase text-slate-400 tracking-widest">Audit Evidence</h4>
                    </div>
                    <div className="aspect-square bg-slate-50 rounded-[2.5rem] overflow-hidden border border-slate-100 shadow-inner group-hover:scale-[1.02] transition-transform duration-500 relative">
                        {actual?.evidencePhoto ? (
                          <img src={actual.evidencePhoto} className="w-full h-full object-cover" />
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-3 text-center p-6">
                            <AlertTriangle className="w-12 h-12" />
                            <p className="text-[10px] font-black uppercase">No Photo</p>
                          </div>
                        )}
                        <div className="absolute top-4 left-4">
                          <span className={`px-3 py-1 rounded-lg text-[8px] font-black text-white uppercase tracking-widest ${finding.level.includes('Critical') ? 'bg-rose-600' : 'bg-slate-900'}`}>{finding.level}</span>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <p className="text-sm font-bold text-slate-800 leading-relaxed pl-4 border-l-4 border-rose-500">"{finding.description}"</p>
                    </div>
                </div>

                <div className="p-10 space-y-6 bg-slate-50/20">
                    <div className="flex items-center gap-3 mb-2">
                        <Zap className="w-5 h-5 text-indigo-500" />
                        <h4 className="text-[11px] font-black uppercase text-slate-400 tracking-widest">Corrective</h4>
                    </div>
                    <div className="aspect-square bg-white rounded-[2.5rem] overflow-hidden border border-slate-200 shadow-sm relative">
                        {finding.correctivePhoto ? (
                          <img src={finding.correctivePhoto} className="w-full h-full object-cover" />
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-4 opacity-30">
                            <Camera className="w-10 h-10" />
                            <p className="text-[10px] font-black uppercase tracking-widest">Pending</p>
                          </div>
                        )}
                    </div>
                    <p className="text-sm font-medium text-slate-600 leading-relaxed italic">{finding.correctiveAction || 'Menunggu tindakan PIC...'}</p>
                </div>

                <div className="p-10 space-y-6 bg-emerald-50/10">
                    <div className="flex items-center gap-3 mb-2">
                        <ShieldCheck className="w-5 h-5 text-emerald-500" />
                        <h4 className="text-[11px] font-black uppercase text-slate-400 tracking-widest">Preventive</h4>
                    </div>
                    <div className="aspect-square bg-white rounded-[2.5rem] overflow-hidden border border-emerald-100 shadow-sm relative">
                        {finding.preventivePhoto ? (
                          <img src={finding.preventivePhoto} className="w-full h-full object-cover" />
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-4 opacity-30">
                            <ImageIcon className="w-10 h-10" />
                            <p className="text-[10px] font-black uppercase tracking-widest">Pending</p>
                          </div>
                        )}
                    </div>
                    <p className="text-sm font-medium text-slate-600 leading-relaxed italic">{finding.preventiveAction || 'Menunggu pencegahan...'}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editingCAPA && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-xl animate-in fade-in duration-300">
              <div className="bg-white rounded-[4rem] w-full max-w-6xl shadow-2xl overflow-hidden scale-in">
                  <div className="p-12 border-b border-slate-100 flex justify-between items-start bg-slate-50">
                      <div>
                          <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Submit CAPA Verification</h3>
                          <p className="text-slate-500 font-medium mt-2 italic">Lengkapi bukti tindakan korektif dan pencegahan.</p>
                      </div>
                      <button onClick={() => setEditingCAPA(null)} className="p-4 text-slate-400 hover:text-slate-900 bg-white rounded-2xl shadow-sm transition-all"><X className="w-8 h-8" /></button>
                  </div>

                  <div className="p-12 grid grid-cols-1 lg:grid-cols-2 gap-16 max-h-[65vh] overflow-y-auto custom-scrollbar">
                      <div className="space-y-8">
                          <h4 className="text-lg font-black uppercase tracking-tighter text-indigo-600 flex items-center gap-3"><Zap className="w-5 h-5" /> Tindakan Korektif</h4>
                          <div 
                            onClick={() => triggerPhotoUpload('corrective')}
                            className="aspect-video bg-slate-50 border-2 border-dashed border-slate-200 rounded-[3rem] flex flex-col items-center justify-center cursor-pointer overflow-hidden relative group hover:border-indigo-500 transition-all shadow-inner"
                          >
                              {capaForm.correctivePhoto ? <img src={capaForm.correctivePhoto} className="w-full h-full object-cover" /> : (
                                <div className="text-center">
                                  {isCompressing && currentUploadTarget === 'corrective' ? (
                                    <Loader2 className="w-10 h-10 animate-spin text-indigo-500" />
                                  ) : (
                                    <>
                                      <Camera className="w-10 h-10 text-slate-300 group-hover:scale-110" />
                                      <p className="text-[10px] font-black text-slate-400 uppercase mt-4">Upload Korektif</p>
                                    </>
                                  )}
                                </div>
                              )}
                          </div>
                          <textarea 
                            value={capaForm.correctiveAction}
                            onChange={e => setCapaForm({...capaForm, correctiveAction: e.target.value})}
                            placeholder="Deskripsi perbaikan langsung..." 
                            className="w-full p-8 bg-slate-50 border border-slate-200 rounded-[2.5rem] h-40 text-sm font-bold outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all shadow-inner"
                          />
                      </div>

                      <div className="space-y-8">
                          <h4 className="text-lg font-black uppercase tracking-tighter text-emerald-600 flex items-center gap-3"><ShieldCheck className="w-5 h-5" /> Tindakan Preventif</h4>
                          <div 
                            onClick={() => triggerPhotoUpload('preventive')}
                            className="aspect-video bg-slate-50 border-2 border-dashed border-slate-200 rounded-[3rem] flex flex-col items-center justify-center cursor-pointer overflow-hidden relative group hover:border-emerald-500 transition-all shadow-inner"
                          >
                              {capaForm.preventivePhoto ? <img src={capaForm.preventivePhoto} className="w-full h-full object-cover" /> : (
                                <div className="text-center">
                                  {isCompressing && currentUploadTarget === 'preventive' ? (
                                    <Loader2 className="w-10 h-10 animate-spin text-emerald-500" />
                                  ) : (
                                    <>
                                      <ImageIcon className="w-10 h-10 text-slate-300 group-hover:scale-110" />
                                      <p className="text-[10px] font-black text-slate-400 uppercase mt-4">Upload Preventif</p>
                                    </>
                                  )}
                                </div>
                              )}
                          </div>
                          <textarea 
                            value={capaForm.preventiveAction}
                            onChange={e => setCapaForm({...capaForm, preventiveAction: e.target.value})}
                            placeholder="Deskripsi sistem pencegahan..." 
                            className="w-full p-8 bg-slate-50 border border-slate-200 rounded-[2.5rem] h-40 text-sm font-bold outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all shadow-inner"
                          />
                      </div>
                  </div>

                  <div className="p-12 bg-slate-50 border-t border-slate-100 flex justify-end gap-6">
                      <button onClick={() => setEditingCAPA(null)} className="px-10 py-5 bg-white border border-slate-200 text-slate-500 rounded-3xl font-black text-xs uppercase tracking-widest">Batal</button>
                      <button 
                        onClick={submitClosureRequest} 
                        disabled={isCompressing || isSubmitting}
                        className="px-16 py-5 bg-slate-900 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-2xl shadow-slate-900/40 hover:bg-emerald-600 transition-all flex items-center gap-3 disabled:opacity-50"
                      >
                          {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                          Kirim Verifikasi Ke Admin
                      </button>
                  </div>
              </div>
              <input type="file" ref={photoInputRef} onChange={handlePhotoUpload} className="hidden" accept="image/*" />
          </div>
      )}

      {reviewingCAPA && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-xl animate-in fade-in duration-300">
              <div className="bg-white rounded-[4rem] w-full max-w-4xl shadow-2xl overflow-hidden scale-in">
                  <div className="p-12 border-b border-slate-100 flex justify-between items-start bg-slate-50">
                      <div>
                          <h3 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Review CAPA Laporan</h3>
                          <p className="text-slate-500 font-medium mt-2 uppercase tracking-widest text-[10px]">Pekerjaan PIC: <span className="text-emerald-600 font-black">{reviewingCAPA.pic}</span></p>
                      </div>
                      <button onClick={() => setReviewingCAPA(null)} className="p-4 text-slate-400 bg-white rounded-2xl shadow-sm hover:text-rose-500 transition-all"><X className="w-8 h-8" /></button>
                  </div>

                  <div className="p-12 space-y-12 max-h-[60vh] overflow-y-auto custom-scrollbar">
                      <div className="grid grid-cols-2 gap-10">
                         <div className="space-y-4">
                            <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest border-b pb-2">Bukti Korektif</h4>
                            <img src={reviewingCAPA.correctivePhoto} className="w-full aspect-video object-cover rounded-3xl border border-slate-200" alt="Corrective" />
                            <p className="text-xs font-bold text-slate-700 italic bg-slate-50 p-4 rounded-xl">"{reviewingCAPA.correctiveAction}"</p>
                         </div>
                         <div className="space-y-4">
                            <h4 className="text-[10px] font-black text-emerald-500 uppercase tracking-widest border-b pb-2">Bukti Preventif</h4>
                            <img src={reviewingCAPA.preventivePhoto} className="w-full aspect-video object-cover rounded-3xl border border-slate-200" alt="Preventive" />
                            <p className="text-xs font-bold text-slate-700 italic bg-slate-50 p-4 rounded-xl">"{reviewingCAPA.preventiveAction}"</p>
                         </div>
                      </div>

                      <div className="p-8 bg-rose-50 rounded-[2.5rem] border border-rose-100 space-y-4">
                        <label className="text-[10px] font-black text-rose-600 uppercase tracking-widest block">Alasan Penolakan (Jika Ada)</label>
                        <textarea 
                          value={adminNote}
                          onChange={e => setAdminNote(e.target.value)}
                          placeholder="Tuliskan alasan jika Anda menolak laporan ini..."
                          className="w-full p-6 bg-white border border-rose-200 rounded-2xl text-sm font-bold outline-none h-32 focus:ring-4 focus:ring-rose-500/10 transition-all shadow-inner"
                        />
                      </div>
                  </div>

                  <div className="p-12 bg-slate-50 border-t border-slate-100 flex justify-between gap-6">
                      <button 
                        onClick={() => handleReviewAction(FollowUpStatus.OPEN)} 
                        className="px-12 py-5 bg-rose-600 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-xl shadow-rose-600/20 hover:bg-rose-700 transition-all flex items-center gap-3"
                      >
                          <ThumbsDown className="w-5 h-5" /> RE-OPEN (TOLAK)
                      </button>
                      <button 
                        onClick={() => handleReviewAction(FollowUpStatus.CLOSED)} 
                        className="px-16 py-5 bg-emerald-600 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-2xl shadow-emerald-600/20 hover:bg-emerald-700 transition-all flex items-center gap-3"
                      >
                          <ThumbsUp className="w-5 h-5" /> APPROVE (CLOSED)
                      </button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default Findings;
